import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { marker as _ } from '@vandres/ngx-translate-extract-marker';
import { LanguageService } from '../../../../core/services/language.service';
import {
  SelectField,
  SelectOption,
} from '../../../../shared/components/form-fields/select-field/select-field';
import { LanguageFlag } from '../../../../shared/components/ui/language-flag/language-flag';
import {
  APP_LANGUAGES,
  isAppLanguage,
  nativeLanguageName,
} from '../../../../shared/i18n/languages';
import { TAppLanguage } from '../../../../shared/schemas/settings';

/** Option id of "Automatic", which follows the browser's languages. */
export const AUTOMATIC_LANGUAGE_ID = 'auto';

/**
 * Interface language picker for the general settings. "Automatic" follows the
 * browser and is tagged with the language that resolves to; the languages are
 * listed by their own names.
 */
@Component({
  selector: 'andor-language-select',
  standalone: true,
  imports: [TranslatePipe, SelectField, LanguageFlag],
  templateUrl: './language-select.html',
  styleUrl: './language-select.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSelect {
  readonly #language = inject(LanguageService);

  protected readonly preference = this.#language.preference;

  readonly options = computed<SelectOption[]>(() => [
    {
      id: AUTOMATIC_LANGUAGE_ID,
      name: _('Automatic'),
      tag: nativeLanguageName(this.#language.browserLanguage()),
    },
    ...APP_LANGUAGES.map(({ id, nativeName }) => ({ id, name: nativeName })),
  ]);

  readonly selected = computed(() => {
    const id = this.preference() ?? AUTOMATIC_LANGUAGE_ID;
    return this.options().find((option) => option.id === id) ?? null;
  });

  protected flagFor(id: string): TAppLanguage | null {
    return isAppLanguage(id) ? id : null;
  }

  select(option: SelectOption): void {
    this.#language.setPreference(this.flagFor(option.id));
  }
}
